import CountUp from 'react-countup';
import {
  AlertCircle,
  BarChart3,
  Clock,
  CheckCircle2,
  TrendingUp,
  Users,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatisticsProps } from '@/types';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import {
  translateCompletedInterviews,
  translateCompletionRate,
  translateStatistics,
  translateMedianTime,
  translateAverageTime,
  translateInterviewsInProgress,
  translateRespondentsLabel,
  translateScreeningAbandoned,
  translateScreeningFailed,
  translateScreeningPassRate,
  translateScreeningPassed,
  translateScreeningQuotaInsights,
  translateScreeningTotalScreened,
} from '../project_results/resultsPageTranslations';
import { resolveTranslation } from '@/utils/resolveTranslation';
// TELV1-581 — shared with the Compare table so the two agree by construction.
import { formatDurationMs as formatTime } from '@/utils/formatDurationMs';

interface CompactStatsProps extends StatisticsProps {
  screeningData?: {
    quota: { current: number; max: number; percentage: number };
    screening: {
      total_screened: number;
      passed: number;
      failed: number;
      abandoned: number;
      pass_rate: number;
    };
  } | null;
  isFiltered?: boolean;
  totalCompletedInterviews?: number;
}

// Hoisted above `CompactStats` so JSX usages don't trip
// @typescript-eslint/no-use-before-define. Pure presentational helpers,
// no behaviour change vs the prior tail-of-file definitions.
function StatRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm text-neutral-600">{label}</span>
      </div>
      <span className="text-sm font-semibold text-neutral-900">{value}</span>
    </div>
  );
}

function StatMini({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-neutral-500">{label}:</span>
      <span className="font-medium text-neutral-700">{value}</span>
    </div>
  );
}

function CompactStats({
  data,
  isLoading,
  screeningData,
  isFiltered,
  totalCompletedInterviews,
}: CompactStatsProps) {
  const { currentProject: singleProjectData } = useCurrentProject();
  const analysisLanguage = singleProjectData?.analysis_language ?? 'English';
  const completed = data?.completed_interviews ?? 0;
  const max = singleProjectData?.respondents_max || 0;
  const started = data?.started_interviews ?? 0;
  const completionRate = data?.completion_rate ?? 0;
  const medianMs = data?.median_time ?? 0;
  const avgMs = data?.average_time ?? 0;

  const showInProgress = started > 0;
  const showCompletionRate = completionRate < 100 && completionRate > 0;

  if (isLoading) {
    return (
      <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5 space-y-4">
        <Skeleton className="h-4 w-24 rounded" />
        <Skeleton className="h-6 w-16 rounded" />
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="h-6 w-20 rounded" />
      </Card>
    );
  }

  if (!data) return null;

  const hasScreening = screeningData && screeningData.screening.total_screened > 0;
  const passRate = screeningData?.screening?.pass_rate ?? 0;
  // TELV1-398: brand-pink fill, not a traffic-light. A low pass rate or a
  // reached quota is not an error, so red is misleading. Keep a subtle amber
  // only for genuine over-recruitment (respondents past 100% of the target).
  const passBarColor = 'bg-tellet-pink';
  const quotaPercentage = screeningData?.quota?.percentage ?? 0;
  const quotaBarColor = quotaPercentage > 100 ? 'bg-amber-500' : 'bg-tellet-pink';

  return (
    <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-4 w-4 text-neutral-400" />
        <h3 className="text-sm font-semibold text-neutral-900">
          {resolveTranslation(translateStatistics, analysisLanguage)}
        </h3>
      </div>

      <div className="flex flex-col gap-3">
        {/* Completed interviews */}
        <StatRow
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
          label={resolveTranslation(translateCompletedInterviews, analysisLanguage)}
          value={
            isFiltered && totalCompletedInterviews ? (
              <>
                <CountUp end={completed} />{' '}
                <span className="text-neutral-400 font-normal">of {totalCompletedInterviews}</span>
              </>
            ) : (
              <>
                <CountUp end={completed} /> / {max}
              </>
            )
          }
        />

        {/* In progress — only if > 0 */}
        {showInProgress && (
          <StatRow
            icon={<Users className="h-4 w-4 text-blue-500" />}
            label={resolveTranslation(translateInterviewsInProgress, analysisLanguage)}
            value={<CountUp end={started} />}
          />
        )}

        {/* Completion rate — only if not 100% */}
        {showCompletionRate && (
          <StatRow
            icon={<TrendingUp className="h-4 w-4 text-violet-500" />}
            label={resolveTranslation(translateCompletionRate, analysisLanguage)}
            value={
              <>
                <CountUp end={completionRate} />%
              </>
            }
          />
        )}

        <div className="border-t border-neutral-100" />

        {/* Median time */}
        <StatRow
          icon={<Clock className="h-4 w-4 text-neutral-400" />}
          label={resolveTranslation(translateMedianTime, analysisLanguage)}
          value={formatTime(medianMs)}
        />

        {/* Average time */}
        <StatRow
          icon={<Clock className="h-4 w-4 text-neutral-400" />}
          label={resolveTranslation(translateAverageTime, analysisLanguage)}
          value={formatTime(avgMs)}
        />

        {/* Screening summary */}
        {hasScreening && (
          <>
            <div className="border-t border-neutral-100" />

            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-4 w-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700">
                {resolveTranslation(translateScreeningQuotaInsights, analysisLanguage)}
              </span>
            </div>

            {/* Pass rate bar */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>{resolveTranslation(translateScreeningPassRate, analysisLanguage)}</span>
                <span className="font-medium">{Math.round(passRate)}%</span>
              </div>
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-200/60">
                <div
                  className={`h-full rounded-full transition-all ${passBarColor}`}
                  style={{ width: `${Math.min(passRate, 100)}%` }}
                />
              </div>
            </div>

            {/* Screening numbers */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <StatMini
                icon={<Users className="h-3 w-3 text-blue-500" />}
                label={resolveTranslation(translateScreeningTotalScreened, analysisLanguage)}
                value={screeningData.screening.total_screened}
              />
              <StatMini
                icon={<CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                label={resolveTranslation(translateScreeningPassed, analysisLanguage)}
                value={screeningData.screening.passed}
              />
              <StatMini
                icon={<XCircle className="h-3 w-3 text-red-500" />}
                label={resolveTranslation(translateScreeningFailed, analysisLanguage)}
                value={screeningData.screening.failed}
              />
              <StatMini
                icon={<AlertCircle className="h-3 w-3 text-amber-500" />}
                label={resolveTranslation(translateScreeningAbandoned, analysisLanguage)}
                value={screeningData.screening.abandoned}
              />
            </div>

            {/* Quota progress */}
            {screeningData.quota.max > 0 && (
              <div className="flex flex-col gap-1 mt-1">
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span>{resolveTranslation(translateRespondentsLabel, analysisLanguage)}</span>
                  <span className="font-medium">
                    {screeningData.quota.current} / {screeningData.quota.max}
                  </span>
                </div>
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-neutral-200/60">
                  <div
                    className={`h-full rounded-full transition-all ${quotaBarColor}`}
                    style={{ width: `${Math.min(quotaPercentage, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

export default CompactStats;
